import { useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { WeekStartPicker } from "./WeekStartPicker";
import { downloadFile, errMsg, isWeekStart } from "./kpiFormat";

export const ExportDialog = ({ defaultStart }) => {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(defaultStart || "");
  const [drafts, setDrafts] = useState(false);
  const [busy, setBusy] = useState(false);

  const go = async () => {
    setBusy(true);
    try {
      const h = await downloadFile("/kpi/export.xlsx", { start, include_unapproved: drafts ? "true" : "" },
        `Geronimo KPI dashboard from ${start}.xlsx`);
      toast.message(`${h.get("x-weeks-included") || 0} week(s) written into the Geronimo template`);
      setOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="sm" onClick={() => { setStart(defaultStart || ""); setOpen(true); }} data-testid="kpi-export-open-btn">
        <Download className="h-4 w-4 mr-1.5" />Export Excel
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="kpi-export-dialog">
          <DialogHeader>
            <DialogTitle>Export the Geronimo spreadsheet</DialogTitle>
            <DialogDescription>
              Figures are written into your Geronimo template — 52 weekly columns starting on the Saturday you choose.
              Only aggregate numbers are included, never client details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>First week (a Saturday)</Label>
              <WeekStartPicker value={start} onChange={setStart} testId="kpi-export-start-input" />
              {start && !isWeekStart(start) && <p className="text-xs text-red-600" data-testid="kpi-export-start-error">Pick a Saturday</p>}
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={drafts} onCheckedChange={(v) => setDrafts(!!v)} data-testid="kpi-export-include-drafts" />
              Include weeks that aren't approved yet (drafts and reviews)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="kpi-export-cancel">Cancel</Button>
            <Button onClick={go} disabled={busy || !isWeekStart(start)} data-testid="kpi-export-download-btn">
              <Download className="h-4 w-4 mr-1.5" />{busy ? "Building…" : "Download .xlsx"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
